import type { GpbEnrolled } from '../../stores/gpbStore';
import type { GpbBinding } from './readEvidence';

/**
 * ⭐⭐ **TWO DEVICES, ONE PATHWAY — MERGED, NEVER OVERWRITTEN.**
 *
 * This is the one place the enrollment row is reconciled, and it is pure so the
 * rule can be tested without a network. Last-write-wins would be a bug with a
 * specific, ugly shape: apply Q01 on a laptop (binding H01–H03) and Q02 on a
 * phone (binding J01), and whichever row was written second erases the other's
 * bindings **while the records themselves stay in the cloud** — because habits,
 * projects, abilities and programs each sync on their own. That is precisely the
 * orphaned-records state the table was created to prevent, reintroduced by the
 * sync that was meant to close it.
 */

/** ⚠️ A binding only ever GROWS. Nothing here removes a key. */
function mergeBinding(mine: GpbBinding, theirs: GpbBinding): GpbBinding {
  const channel = (a: Readonly<Record<string, string>> = {}, b: Readonly<Record<string, string>> = {}) =>
    /* ⚠️⚠️ `b` (the server) wins a genuine conflict — the same authored id bound to
       two different records means two devices each created one, and picking the
       shared copy leaves the local duplicate visible rather than hiding it. It
       cannot be silently reconciled: both records exist and both are real. */
    ({ ...a, ...b });
  return {
    quests: channel(mine.quests, theirs.quests),
    habits: channel(mine.habits, theirs.habits),
    skills: channel(mine.skills, theirs.skills),
    projects: channel(mine.projects, theirs.projects),
    modules: channel(mine.modules, theirs.modules),
  };
}

/**
 * ⚠️⚠️ **A WINDOW TAKES THE EARLIER DATE, ALWAYS.** It is the floor that
 * `distinct_dates` counts from, so moving it later would un-count days the person
 * has already recorded — work disappearing because they opened a second device.
 * Moving it earlier can only ever count more, which is the safe direction.
 */
function mergeWindows(
  mine: Readonly<Record<number, string>>, theirs: Readonly<Record<number, string>>,
): Record<number, string> {
  const out: Record<number, string> = { ...mine };
  for (const [rank, date] of Object.entries(theirs)) {
    const n = Number(rank);
    out[n] = out[n] && out[n] < date ? out[n] : date;
  }
  return out;
}

export function mergeEnrolled(mine: GpbEnrolled | undefined, theirs: GpbEnrolled): GpbEnrolled {
  if (!mine) return theirs;
  return {
    ...theirs,
    enrollment: {
      ...theirs.enrollment,
      /* ⚠️ The version is the taker's pin (D23). Keep the one already held — a merge
         must never be the thing that moves somebody onto a release they did not
         accept. */
      version: mine.enrollment.version,
      windows: mergeWindows(mine.enrollment.windows, theirs.enrollment.windows),
      binding: mergeBinding(mine.enrollment.binding, theirs.enrollment.binding),
    },
    /* ⚠️ Set aside is a per-person decision, not per-device: the server's answer
       wins so putting it away on one device puts it away everywhere. */
    aside: theirs.aside ?? mine.aside,
    commitment: theirs.commitment ?? mine.commitment,
    returnPlan: mergeReturnPlan(mine.returnPlan, theirs.returnPlan),
  };
}

/**
 * ⚠️⚠️ **THE LATER ARRANGEMENT WINS — WHICH IS THE OPPOSITE OF `windows` ABOVE.**
 *
 * A window is a FLOOR that `distinct_dates` counts from, so taking the earlier
 * date is the only safe direction: moving it forward would un-count days somebody
 * already recorded. An arrangement is a CURRENT INTENTION, written in their own
 * words, and the newest is the one they mean. Taking the older would quietly
 * restore a plan they had already replaced.
 *
 * ⚠️⚠️ **WHOLESALE, NEVER FIELD BY FIELD.** Merging part-by-part across two saves
 * would assemble a plan neither version ever said — this device's trigger against
 * that device's return action, describing a route the person never wrote. The four
 * parts are one statement and they move together.
 *
 * ⚠️ Equal dates resolve to the server's copy, matching the binding's conflict
 * rule: two saves on one day cannot be ordered, and picking deterministically
 * beats picking by whichever sync happened to run second.
 */
function mergeReturnPlan(
  mine: GpbEnrolled['returnPlan'], theirs: GpbEnrolled['returnPlan'],
): GpbEnrolled['returnPlan'] {
  if (!mine) return theirs;
  if (!theirs) return mine;
  return theirs.savedOn >= mine.savedOn ? theirs : mine;
}
